import { useEffect } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { useAuthStore } from "@/stores/auth-store"
import { OrgDeactivatedScreen } from "@/components/OrgDeactivatedScreen"
import { loginUrlFor } from "@/lib/redirect"
import { Loader2 } from "lucide-react"

interface ProtectedRouteProps {
  children: React.ReactNode
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation()
  const {
    isAuthenticated,
    isLoading,
    isOrgActive,
    isPlatformAdmin,
    aal,
    mfaEnrolled,
    mfaRequired,
    initialize,
  } = useAuthStore()

  // Initialize auth state on mount (restore session from storage)
  useEffect(() => {
    void initialize()
  }, [initialize])

  // R5 — gating order is load-bearing.
  //   1. Loading spinner
  //   2. Not authenticated → /login
  //   3. Org paused (non-platform-admin) → deactivation screen
  //         MUST come before MFA gate — a paused-org admin would otherwise be
  //         redirected to /mfa-enroll where they can't fix the org.
  //   4. mfaRequired but not enrolled → /mfa-enroll
  //   5. mfaEnrolled but session is aal1 → /mfa-challenge
  //   6. Render children

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Carry the requested location twice: router state (survives an SPA bounce)
    // and a `?redirect=` param (survives the full page load a deep link from a
    // notification email produces). Both are re-validated on the login side.
    return <Navigate to={loginUrlFor(location)} state={{ from: location }} replace />
  }

  if (!isOrgActive && !isPlatformAdmin) {
    return <OrgDeactivatedScreen />
  }

  if (mfaRequired && !mfaEnrolled) {
    // First login after org-wide MFA is switched on is a likely deep-link path,
    // so the enrol detour has to carry the destination too.
    return <Navigate to="/mfa-enroll" state={{ from: location }} replace />
  }

  if (aal === 'aal1' && mfaEnrolled) {
    return <Navigate to="/mfa-challenge" state={{ from: location }} replace />
  }

  return <>{children}</>
}
